/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import { Field } from 'redux-form'
import classNames from 'classnames/bind'
import { MAX_LENGTHS } from 'constants/forms'
import { EMPTY_ARRAY } from 'constants/frozenObjects'
import { Text as TextType, GetTextPath } from 'hocs/localization'
import { Input, Captcha, Checkbox } from 'apps/FormFields'
import FormErrorMessage from 'apps/FormErrorMessage'
import Button from 'elements/Button'
import {
  createValidation,
  createValidator,
  hasValidEmail,
  hasValidPassword,
  hasPasswordUppercaseLetters,
  hasPasswordLowercaseLetters,
  hasPasswordNumbers,
  hasPasswordSpecialCharacters,
  hasPasswordValidLength,
  removeSpaces,
  hasRequiredCheckbox
} from 'utils/form'
import LocalizationText from 'apps/LocalizationText'
import { STEPS, FIELD_NAMES } from './constants'
import * as styles from './SignUpForm.scss'

const cx = classNames.bind(styles)

interface IProps {
  t: TextType,
  getTextPath: GetTextPath,
  handleSubmit: any,
  valid: boolean,
  submitting: boolean,
  isCaptchaVerified: boolean,
  onVerifyCaptcha: (response: string) => void,
  captchaRef: (ref: React.ReactElement<any>) => React.Ref<any>
  onSubmit: (formValues: any) => void,
  captchaFieldName: string,
  step: STEPS,
  countryResidenceOptions: any[],
  onChangeCountry: () => void,
  errorMessages: string[],
  showConfirmationMessage: boolean,
  onSetErrorMessages: (errorMessages: string[]) => void,
  isCaptchaEnabled: boolean,
  isCaptchaWhiteListed: boolean,
}

class SignUpForm extends React.Component<IProps> {
  public requiredCheckboxValidation = createValidation([
    createValidator(hasRequiredCheckbox, ''),
  ])

  public emailValidation = createValidation([
    createValidator(hasValidEmail, this.props.getTextPath('errors.email')),
  ])

  public passwordValidation = createValidation([
    createValidator(hasValidPassword, this.props.getTextPath('errors.password')),
    createValidator(hasPasswordUppercaseLetters, this.props.getTextPath('errors.password.uppercaseLetter')),
    createValidator(hasPasswordLowercaseLetters, this.props.getTextPath('errors.password.lowercaseLetter')),
    createValidator(hasPasswordNumbers, this.props.getTextPath('errors.password.number')),
    createValidator(hasPasswordSpecialCharacters, this.props.getTextPath('errors.password.specialCharacter')),
    createValidator(hasPasswordValidLength, this.props.getTextPath('errors.password.length')),
  ])

  private onChange = () => {
    this.props.onSetErrorMessages(EMPTY_ARRAY)
  }

  public render () {
    const {
      t,
      handleSubmit,
      submitting,
      onVerifyCaptcha,
      captchaRef,
      onSubmit,
      captchaFieldName,
      errorMessages,
      showConfirmationMessage,
      isCaptchaEnabled,
      isCaptchaWhiteListed,
      valid,
    } = this.props

    return (
      <form className={styles.root} onSubmit={handleSubmit(onSubmit)}>
        <Field
          disabled={submitting}
          name='email'
          type='text'
          component={Input}
          validate={this.emailValidation}
          maxLength={MAX_LENGTHS.email}
          normalize={removeSpaces}
          label={t('emailPlaceholder')}
          onChange={this.onChange}
          required
        />
        <Field
          disabled={submitting}
          name='password'
          type='password'
          component={Input}
          validate={this.passwordValidation}
          maxLength={MAX_LENGTHS.password}
          normalize={removeSpaces}
          label={t('passwordPlaceholder')}
          onChange={this.onChange}
          required
          withPrompt
          newLineError
        />
        <div className={styles.termsCheckbox}>
          <Field
            disabled={submitting}
            type='text'
            name={FIELD_NAMES.termsAccepted}
            component={Checkbox}
            label={
              (<LocalizationText>
                {t('termsAccepted')}
              </LocalizationText>)
              }
            validate={this.requiredCheckboxValidation}
          />
        </div>
        {(isCaptchaEnabled && !isCaptchaWhiteListed) && (
          <div className={cx(styles.formField, styles.captcha)}>
            <Field
              captchaRef={captchaRef}
              name={captchaFieldName}
              component={Captcha}
              verifyCallback={onVerifyCaptcha}
            />
          </div>
        )}
        <FormErrorMessage messages={errorMessages} />
        {(showConfirmationMessage) && (
          <div className={styles.confirmationLinkSent}>
            {t('alerts.signUpSuccesfull.message')}
          </div>
        )}
        <div className={cx(styles.formField, styles.StepButton)}>
          <Button
            id='createAccountButton'
            fluid
            disabled={submitting || !valid}
          >
            {submitting ? t('submitButtonSubmitting') : t('next')}
          </Button>
        </div>
      </form>
    )
  }
}

export default SignUpForm
